import { createColumnHelper, ColumnDef } from "@tanstack/react-table";
import { twMerge } from "tailwind-merge";

import { useGetUserType } from "@/modules/auth/hooks/userType.hooks";
import history from "@/routes/history";

import { AddEntityButton } from "../components/TreeTable/AddEntityButton";
import { TableHeader } from "../components/TreeTable/TableHeader";
import { ToggleArrow } from "../components/TreeTable/ToggleArrow";
import { ManagementType, OrganizationNode } from "../types/commonTypes";

export const useManagementTableColumns = () => {
  const columnHelper = createColumnHelper<OrganizationNode>();
  const { isSuperAdmin, canCreateGroup } = useGetUserType();

  const columns = [
    columnHelper.accessor("name", {
      header: () => (
        <TableHeader text="Groupe / Agence / Équipe / Utilisateur" />
      ),
      cell: ({ row }) => {
        const type = row.original.type;
        const icon = {
          [ManagementType.GROUP]: "pi pi-building",
          [ManagementType.AGENCY]: "pi pi-home",
          [ManagementType.TEAM]: "pi pi-users",
          [ManagementType.USER]: "pi pi-user",
        }[type];

        const typeText = {
          [ManagementType.GROUP]: "Groupe",
          [ManagementType.AGENCY]: "Agence",
          [ManagementType.TEAM]: "Équipe",
          [ManagementType.USER]: "Utilisateur",
        }[type];

        const showInvoiceIndicator =
          row.getCanExpand() &&
          (row.original.type === ManagementType.GROUP ||
            row.original.type === ManagementType.AGENCY);

        const allAgenciesHaveInvoicesSet = row.original.children?.every(
          (child) => {
            return (
              child.customer?.offerTypeId &&
              child.customer?.invoiceFrequencyEdl &&
              child.customer?.invoiceFrequencyErp
            );
          }
        );

        const customerHasItsInvoiceSet = !!(
          row.original.customer?.offerTypeId &&
          row.original.customer?.invoiceFrequencyEdl &&
          row.original.customer?.invoiceFrequencyErp
        );

        const invoiceSettingsExist =
          allAgenciesHaveInvoicesSet || customerHasItsInvoiceSet;

        return (
          <div
            className={twMerge(
              "flex items-center flex-wrap-reverse gap-x-2.5 gap-y-2"
              // !row.getCanExpand() && "block"
            )}
            style={{
              paddingLeft: `${row.depth * (row.original.type === ManagementType.USER ? 36 : 30.75)}px`,
            }}
          >
            <div className="flex items-center gap-2.5">
              {row.getCanExpand() && (
                <ToggleArrow
                  open={row.getIsExpanded()}
                  onClick={row.toggleExpanded}
                />
              )}
              {isSuperAdmin && showInvoiceIndicator && (
                <i
                  className={twMerge(
                    "bi text-lg",
                    invoiceSettingsExist && "bi-check-circle-fill text-success",
                    !invoiceSettingsExist &&
                      "bi-exclamation-circle-fill text-red-FF0000"
                  )}
                />
              )}
              <div
                className={twMerge(
                  "flex items-center gap-1.5",
                  !row.getCanExpand() && "ml-1"
                )}
              >
                <div className="mt-[4.75px]">
                  <i className={icon} />
                </div>
                <div className="mt-1">
                  <span className="text-gray-500 mr-[5px]">{typeText}</span>
                  <span className="font-bold">{row.original.name}</span>
                </div>
              </div>
            </div>
            {row.original.isDisabled && (
              <div className="mt-1 px-1.5 h-[20.6px] flex items-center justify-center text-[11.25px] text-red-FF0000 font-extrabold border border-red-FF0000 rounded">
                Bloqué
              </div>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("role", {
      header: () => <TableHeader text="Rôle" />,
      cell: ({ row }) => {
        return (
          <div className="text-gray-500 font-bold">{row.original.role}</div>
        );
      },
    }),
    columnHelper.accessor("sector", {
      header: () => <TableHeader text="Secteur" />,
      cell: ({ row }) => {
        return (
          <div className="text-gray-500 font-bold">
            {row.original.sector?.join(", ")}
          </div>
        );
      },
    }),
    columnHelper.accessor("actions", {
      header: () =>
        canCreateGroup ? (
          <div className="absolute right-4 top-[11.5px]">
            <AddEntityButton
              text="Ajouter un groupe"
              onClick={() => {
                history.push("/management/group");
              }}
            />
          </div>
        ) : undefined,
    }),
  ];

  return { columns: columns as ColumnDef<OrganizationNode>[] };
};
